import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import CrmPageLayout from '../../shared/components/CrmPageLayout';
import GlobalSearch from './GlobalSearch';
import KpiRow from './KpiRow';
import DealPipeline from './DealPipeline';
import MyTasks from './MyTasks';
import RecentlyAddedContacts from './RecentlyAddedContacts';
import RecentActivity from './RecentActivity';
import crmLabels from '../../shared/utils/crmLabels';
import '../styles/crmDashboardStyles.css';

const axios = require('axios').default;

function CrmDashboardPage({ team, csrfToken }) {
  const labels = crmLabels(team);
  const dealsEnabled = (team && team.deals_enabled) !== false;
  const [summary, setSummary] = useState({ kpis: {}, pipeline: [] });
  const [tasks, setTasks] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contactsError, setContactsError] = useState(null);

  const teamId = team && team.id;

  useEffect(() => {
    if (!teamId) return undefined;
    if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    let active = true;
    setLoading(true);
    const base = `/teams/${teamId}`;
    Promise.allSettled([
      axios.get(`${base}/crm_dashboard_summary.json`),
      axios.get(`${base}/crm_dashboard_tasks.json`),
      axios.get(`${base}/crm_dashboard_contacts.json`, { params: { per_page: 10, page: 1 } }),
      axios.get(`${base}/crm_dashboard_activity.json`),
    ]).then(([s, t, c, a]) => {
      if (!active) return;
      if (s.status === 'fulfilled') setSummary(s.value.data || { kpis: {}, pipeline: [] });
      if (t.status === 'fulfilled') setTasks((t.value.data || {}).tasks || []);
      if (c.status === 'fulfilled') setContacts((c.value.data || {}).contacts || []);
      else setContactsError('Failed to load contacts');
      if (a.status === 'fulfilled') setActivity((a.value.data || {}).activity || []);
      setLoading(false);
    });
    return () => { active = false; };
  }, [teamId, csrfToken]);

  return (
    <CrmPageLayout
      team={team}
      activeSidebarKey="dashboard"
      mainTopMargin={32}
      title="CRM"
      subtitle={dealsEnabled ? `${labels.contact.plural}, ${labels.organization.plural.toLowerCase()}, ${labels.deal.plural.toLowerCase()}, and tasks` : `${labels.contact.plural}, ${labels.organization.plural.toLowerCase()}, and tasks`}
      secondaryAction={(
        <>
          <a className="btn btn-setlist-secondary btn-sm" href={`/teams/${teamId}/crm/fans?action=import`}>Import</a>
          <a className="btn btn-setlist-primary btn-sm" href={`/teams/${teamId}/crm/fans?action=new`}>{`+ New ${labels.contact.singular.toLowerCase()}`}</a>
        </>
      )}
    >
      <div className="crm-dash__search">
        <GlobalSearch teamId={teamId} labels={labels} />
      </div>

      <KpiRow kpis={summary.kpis} loading={loading} teamId={teamId} labels={labels} showDeals={dealsEnabled} />

      <div className="crm-dash__grid">
        <div className="crm-dash__col">
          {dealsEnabled && <DealPipeline pipeline={summary.pipeline} teamId={teamId} loading={loading} labels={labels} />}
          <RecentlyAddedContacts contacts={contacts} teamId={teamId} loading={loading} error={contactsError} labels={labels} />
        </div>
        <div className="crm-dash__col">
          <MyTasks tasks={tasks} teamId={teamId} loading={loading} />
          <RecentActivity activity={activity} loading={loading} labels={labels} />
        </div>
      </div>
    </CrmPageLayout>
  );
}

CrmDashboardPage.propTypes = {
  team: PropTypes.object,
  csrfToken: PropTypes.string,
};

export default CrmDashboardPage;
